'use client';

import CustomButton from '@/components/custom-button';
import CustomImageWithFallback from '@/components/custom-image-with-fallback';
import { APP_ASSETS, ICONS } from '@/constant/assets';
import { pageDirectories, pageDirectoryParameter } from '@/constant/route';
import { useURLState } from '@/hooks/use-url-state';
import { isEnumValue } from '@/lib/utils';
import { generateSsoRedirectToClient } from '@/server/api/v1/auth/auth.action';
import { AuthErrorCode } from '@/server/config/auth.error';
import { LoginMethod, LoginTarget } from '@/types/enum';
import { signIn } from 'next-auth/react';
import { useTranslations } from 'next-intl';
import React, { memo, useCallback } from 'react';
import { RenderAlertLogin } from './alert-login';

const page_path = pageDirectories['Signin'];
const key_subject = pageDirectoryParameter[page_path].subject!;
const key_code = pageDirectoryParameter[page_path].code!;
const key_email = pageDirectoryParameter[page_path].email!;
const key_msg = pageDirectoryParameter[page_path].message!;
const key_login_target = pageDirectoryParameter[page_path].loginTarget!;

export const Render3rdPartyLogin = React.memo(
    (props: {
        isDisabledBtn?: boolean;
        loginMethod: Record<LoginMethod, boolean>;
        /**
         * Đối tượng đăng nhập đang chọn (tab Sinh viên / Cán bộ / Vãng lai).
         * Chỉ ảnh hưởng tới login Google: gắn kèm vào callbackUrl để callback
         * signIn() (auth.config.ts) đọc lại và định tuyến API tương ứng.
         */
        loginTarget?: LoginTarget;
    }) => {
        const {
            loginMethod = {
                ADFS: false,
                Google: false,
                Office365: false,
                SSO: false,
            },
            isDisabledBtn,
            loginTarget,
        } = props;

        const [objLoading, setObjLoading] = React.useState<
            Record<string, boolean>
        >({});

        const [subject] = useURLState(key_subject);
        const [error_code] = useURLState(key_code);
        const [error_email] = useURLState(key_email);
        const [error_msg] = useURLState(key_msg);

        const transPage = useTranslations('SignIn');

        const popupCenter = React.useCallback((url: string, title: string) => {
            const dualScreenLeft = window.screenLeft ?? window.screenX;
            const dualScreenTop = window.screenTop ?? window.screenY;

            const width =
                window.innerWidth ??
                document.documentElement.clientWidth ??
                screen.width;

            const height =
                window.innerHeight ??
                document.documentElement.clientHeight ??
                screen.height;

            const systemZoom = width / window.screen.availWidth;

            const left = (width - 500) / 2 / systemZoom + dualScreenLeft;
            const top = (height - 550) / 2 / systemZoom + dualScreenTop;

            const newWindow = window.open(
                url,
                title,
                `width=${500 / systemZoom},height=${
                    550 / systemZoom
                },top=${top},left=${left}`
            );

            if (newWindow) {
                newWindow.focus();

                // Kiểm tra định kỳ xem popup có bị đóng không
                const checkClose = setInterval(() => {
                    if (newWindow.closed) {
                        clearInterval(checkClose);
                        console.log('Popup closed');
                        setObjLoading((prev) => ({ ...prev, Google: false }));
                    }
                }, 500); // Kiểm tra mỗi 500ms

                // Lắng nghe message từ popup
                window.addEventListener('message', (event) => {
                    if (event.origin !== window.location.origin) return; // Bảo mật: Chỉ nhận từ cùng origin
                    if (event.data === 'AUTH_SUCCESS') {
                        console.log('Auth successful, reloading...');
                        clearInterval(checkClose);
                        window.location.reload();
                    }
                });
            }
        }, []);

        const onLoginSSO = useCallback(async () => {
            // Step 1: Generate a secure random code_verifier
            const current_time = new Date().getTime().toString();
            const result = await generateSsoRedirectToClient({
                state: current_time,
            });
            if (result?.data) {
                window.location.href = result?.data;
            }
        }, []);

        const onLoginWith3rdParty = React.useCallback(
            async (method: LoginMethod) => {
                setObjLoading((x) => ({
                    ...x,
                    [method]: true,
                }));

                switch (method) {
                    case LoginMethod.Google: {
                        const url = new URL(window.location.href);
                        if (loginTarget) {
                            url.searchParams.set(
                                key_login_target,
                                loginTarget
                            );
                        }
                        signIn('google', { callbackUrl: url.toString() });
                        break;
                    }
                    case LoginMethod.Office365:
                        break;
                    case LoginMethod.ADFS:
                        break;
                }
            },
            [loginTarget]
        );

        /**Có ít nhất 1 phương thức login 3rd */
        const hvLeastOneMethod = Object.keys(loginMethod).some((x) => {
            return isEnumValue(LoginMethod, x) && loginMethod[x];
        });

        /**
         * Các phương thức login dạng icon (Google / Office365 / ADFS) —
         * gom vào 1 hàng icon (tái sử dụng layout preview TEMP cũ). Lọc
         * theo `loginMethod` để chỉ hiện những phương thức được bật. Nút
         * SSO KHÔNG nằm ở đây — vẫn render như nút full-width bên dưới.
         */
        const iconMethods = (
            [
                {
                    method: LoginMethod.Google,
                    src: ICONS.google,
                    label: transPage('SignInWithGoogle'),
                },
                {
                    method: LoginMethod.Office365,
                    src: ICONS[365],
                    label: transPage('SignInWithOffice365'),
                },
                {
                    method: LoginMethod.ADFS,
                    src: ICONS.adfs,
                    label: transPage('SignInWithADFS'),
                },
            ] as const
        ).filter((x) => loginMethod[x.method]);

        return (
            <>
                {hvLeastOneMethod && (
                    <div className='my-6 flex select-none items-center justify-center gap-2 text-sm text-muted-foreground'>
                        <span className='block h-0.5 flex-1 bg-gradient-to-r from-primary/20 to-primary/60'></span>
                        <span className='font-semibold'>
                            {transPage('OrSignInWith')}
                        </span>
                        <span className='block h-0.5 flex-1 bg-gradient-to-l from-primary/20 to-primary/60'></span>
                    </div>
                )}

                <div className='space-y-4 text-sm'>
                    {/* Hàng icon các phương thức 3rd-party (Google / Office365 /
                        ADFS) — chỉ hiện những phương thức được bật. */}
                    {iconMethods.length > 0 && (
                        <div className='flex items-stretch gap-2 rounded-md border-2 border-dashed border-primary/20'>
                            {iconMethods.map((x) => (
                                <button
                                    key={x.method}
                                    type='button'
                                    disabled={
                                        isDisabledBtn || objLoading[x.method]
                                    }
                                    onClick={() =>
                                        onLoginWith3rdParty(x.method)
                                    }
                                    title={x.label}
                                    className='flex min-w-0 flex-1 basis-0 flex-col items-center gap-1 rounded-md p-1 transition hover:bg-primary/5 disabled:opacity-50'
                                >
                                 <div className='flex items-center gap-2'>
                                       <CustomImageWithFallback
                                        src={x.src}
                                        width={28}
                                        height={28}
                                        alt={`logo-${x.method}`}
                                        className={
                                            objLoading[x.method]
                                                ? 'h-7 w-7 animate-pulse'
                                                : 'h-7 w-7'
                                        }
                                    />
                                    <span className='line-clamp-1 text-xs text-muted-foreground'>
                                        {x.label}
                                    </span>
                                 </div>
                                </button>
                            ))}
                        </div>
                    )}

                    {loginMethod[LoginMethod.SSO] && (
                        <CustomButton.Root
                            type='button'
                            variant={'ghost'}
                            onClick={onLoginSSO}
                            disabled={isDisabledBtn}
                            title={transPage('SignInWithSSO')}
                            className='w-full flex-1 gap-x-2 border-[2px] border-primary/20 px-3 font-semibold shadow-md'
                            icon={
                                <CustomImageWithFallback
                                    src={ICONS.sso}
                                    width={20}
                                    height={20}
                                    alt='logo-sso'
                                    className='h-6 w-6'
                                />
                            }
                        >
                            {transPage('SignInWithSSO')}
                        </CustomButton.Root>
                    )}
                </div>

                <RenderAlertLogin
                    isShow={!!(subject && error_code)}
                    title={`${transPage('SignIn')} ${subject.toUpperCase()} ${transPage('Failed').toLowerCase()}!`}
                    desc={
                        <ErrorDescription
                            error_code={error_code as AuthErrorCode}
                            error_email={error_email}
                            error_msg={error_msg}
                        />
                    }
                />
            </>
        );
    }
);
Render3rdPartyLogin.displayName = 'Render3rdPartyLogin';

const ErrorDescription = memo(
    ({
        error_code,
        error_email,
        error_msg = '',
    }: {
        error_code: AuthErrorCode;
        error_email?: string;
        error_msg?: string;
    }) => {
        const transPage = useTranslations('SignIn');
        const transDef = useTranslations('System');

        switch (error_code) {
            case AuthErrorCode.Google_NotFound:
                return `${transPage('AccountNotFound')} ${error_email}`;

            case AuthErrorCode.Google_BusinessRequired:
                return `${decodeURIComponent(error_msg) ?? error_code}`;

            case AuthErrorCode.InvalidCredentials:
                return transPage('AccountInfoIncorrect');

            case AuthErrorCode.Forbidden:
                return transPage('AccountDoesNotHaveAccess');

            case AuthErrorCode.SSO_NotProvisioned:
                return error_msg
                    ? decodeURIComponent(error_msg)
                    : transPage('SsoNotProvisioned');

            case AuthErrorCode.SSO_Unavailable:
                return transPage('SsoUnavailable');

            default:
                `${transPage('ErrorCode')}: ${error_code}`;
        }
    }
);

ErrorDescription.displayName = 'ErrorDescription';
